// Libraries
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Stack } from "@mui/material";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useSnackbar } from "@/Common/hooks/Snackbar/useSnackbar";

// Contexts
import { usePayrollMappingsContext } from "@/Settings/context/PayrollMapping";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// API
import { getCustomFieldDefinitionsByLocationId, getMappedCustomFields } from "@/Settings/api/PayrollMappings/queries";
import { updateCustomFieldDefinitions, updateMappedCustomFields } from "@/Settings/api/PayrollMappings/mutations";

// Layouts
import CountDownButton from "@/Layouts/components/Buttons/CountDownButton";

// Components
import CustomFieldItemsSkeleton from "@/Settings/components/PayrollMapping/Tabs/CustomFieldItems/Skeleton";
import HeaderSection from "./HeaderSection";
import MappingSection from "./MappingSection";

// Constants
import ACTIONS from "@Constants/actionsList";
import { PAYROLL_MAPPING_KEYS } from "@/Settings/Constants/PayrollMapping";

// Types
import { IMappedCustomField, ISystemSettingsMappedCustomField } from "@/Settings/types/PayrollMapping/CustomFieldItemsMapping/api";

// Helpers
import { areAllInputsFulfilled } from "@/Settings/helpers/PayrollMapping";

type TPayItemsProps = {
	isActiveTab: boolean;
};

export default function CustomFieldItems(props: Readonly<TPayItemsProps>) {
	const { isActiveTab } = props;
	const { locationId = "" } = usePayrollMappingsContext();
	const queryClient = useQueryClient();
	const { showSnackBar } = useSnackbar();

	const { useAuth } = useAuthorization();
	const editAccess = useAuth(ACTIONS.EDIT_CUSTOM_FIELD_ITEMS_MAPPING);

	const { getSubscription } = useSubscriptions();

	/******************************************* STATES *******************************************/
	const [allMappedCustomFields, setAllMappedCustomFields] = useState<ISystemSettingsMappedCustomField[]>([]);

	/******************************************* QUERIES *******************************************/
	const { data: customFieldDefinitions = [], isLoading: isLoadingCustomFieldItem } = useQuery({
		queryKey: [PAYROLL_MAPPING_KEYS.QUERY.CUSTOM_FIELD_DEFINITIONS, locationId],
		queryFn: () => getCustomFieldDefinitionsByLocationId(locationId),
		enabled: Boolean(locationId) && isActiveTab,
	});

	const { data: mappedCustomFields = [], isLoading: isLoadingMappedCustomFields } = useQuery({
		queryKey: [PAYROLL_MAPPING_KEYS.QUERY.MAPPED_CUSTOM_FIELDS, locationId],
		queryFn: async () => {
			const { mappedCustomFields: response } = await getMappedCustomFields(locationId);
			return response;
		},
		enabled: Boolean(locationId) && isActiveTab,
	});

	/******************************************* MUTATIONS *******************************************/
	const { mutate: applyMapping, isPending: isPendingApplyingMapping } = useMutation({
		mutationKey: [PAYROLL_MAPPING_KEYS.MUTATION.UPDATE_MAPPED_CUSTOM_FIELDS, locationId],
		mutationFn: (input: Omit<ISystemSettingsMappedCustomField, "_id">[]) => updateMappedCustomFields(locationId, input),
		onSettled: (data, error) => {
			if (!error) {
				showSnackBar("Custom field mapping applied");
			} else {
				showSnackBar("System error.", true);
			}
			const { mappedCustomFields } = data as IMappedCustomField;
			queryClient.setQueryData([PAYROLL_MAPPING_KEYS.QUERY.MAPPED_CUSTOM_FIELDS, locationId], mappedCustomFields);
		},
	});

	const { mutate: updateCustomFieldDefinitionsMutation, isPending: isUpdatingCustomFieldDefinitions } = useMutation({
		mutationFn: () => updateCustomFieldDefinitions(locationId),
	});

	/******************************************* SUBSCRIPTIONS ***************************************/

	const CustomFieldDefSubscription = getSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.CUSTOM_FIELD_DEFINITIONS, locationId]);
	CustomFieldDefSubscription?.subscriptions?.onUpdateCustomFieldDefinitions?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: [PAYROLL_MAPPING_KEYS.QUERY.CUSTOM_FIELD_DEFINITIONS, locationId],
		});
	});

	const mappedCustomFieldsSubscription = getSubscription([PAYROLL_MAPPING_KEYS.SUBSCRIPTION.MAPPED_CUSTOM_FIELDS, locationId]);
	mappedCustomFieldsSubscription?.subscriptions?.onUpdateMappedCustomFields?.setCallback(
		(response: { data: { onUpdateMappedCustomFields: IMappedCustomField } }) => {
			const { mappedCustomFields: responseMappedCustomFields = [] } = response.data?.onUpdateMappedCustomFields ?? {};
			queryClient.setQueryData([PAYROLL_MAPPING_KEYS.QUERY.MAPPED_CUSTOM_FIELDS, locationId], responseMappedCustomFields);
		}
	);

	/******************************************* HANDLERS *******************************************/
	const handleDiscardChanges = () => {
		const cacheMappedCustomFields = queryClient.getQueryData<ISystemSettingsMappedCustomField[]>([
			PAYROLL_MAPPING_KEYS.QUERY.MAPPED_CUSTOM_FIELDS,
			locationId,
		]);
		setAllMappedCustomFields(cacheMappedCustomFields ?? []);
	};

	const handleApplyMapping = () => {
		applyMapping(
			allMappedCustomFields.map(({ _id, ...rest }) => ({
				...rest,
			}))
		);
	};

	/******************************************* EFFECTS *******************************************/
	useEffect(() => {
		if (mappedCustomFields?.length) {
			setAllMappedCustomFields(mappedCustomFields);
		}
	}, [mappedCustomFields, isActiveTab, locationId]);

	const isLoading = isLoadingCustomFieldItem || isLoadingMappedCustomFields;
	if (isLoading) {
		return <CustomFieldItemsSkeleton />;
	}

	const disabled = !editAccess || isPendingApplyingMapping;
	const allInputsFulfilled = areAllInputsFulfilled(allMappedCustomFields);

	return (
		<Stack minHeight={"540px"} justifyContent={"space-between"}>
			<Box>
				{/* Header Section*/}
				<HeaderSection
					disabled={{
						discardButton: disabled,
						applyButton: disabled || !allInputsFulfilled,
					}}
					isSaving={isPendingApplyingMapping}
					handleDiscardChanges={handleDiscardChanges}
					handleApplyMapping={handleApplyMapping}
				/>
				{/* List Headers */}
				<MappingSection
					customFieldsOptions={customFieldDefinitions}
					disabled={disabled}
					mappedCustomFields={allMappedCustomFields}
					setMappedCustomFields={setAllMappedCustomFields}
				/>
			</Box>
			<Box>
				<CountDownButton
					disabled={isUpdatingCustomFieldDefinitions || disabled}
					showButton
					typographySx={{ fontSize: "12px" }}
					initialCopy={"Update Now"}
					actionCopy={"Updating..."}
					loading={isUpdatingCustomFieldDefinitions}
					counter={60}
					onClickCallBack={() => updateCustomFieldDefinitionsMutation()}
				/>
			</Box>
		</Stack>
	);
}
